import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { navState } from '../services/pageCache';
import './TasksPage.css';

function TasksPage({ currentSellerId }) {
  const sid = currentSellerId || null;
  const [tasks, setTasks] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filtros — inicializam do navState se existir, senão usam defaults
  const [filterStatus, setFilterStatus] = useState(() => navState.get(sid, 'tarefas', 'filterStatus') || 'active');
  const [filterAssignedTo, setFilterAssignedTo] = useState(() => navState.get(sid, 'tarefas', 'filterAssignedTo') || '');
  const [filterCreatedBy, setFilterCreatedBy] = useState(() => navState.get(sid, 'tarefas', 'filterCreatedBy') || '');
  const [filterDue, setFilterDue] = useState(() => navState.get(sid, 'tarefas', 'filterDue') || null);
  const [search, setSearch] = useState(() => navState.get(sid, 'tarefas', 'search') || '');
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toastTimer = useRef(null);

  useEffect(() => {
    api.getSellers().then(setSellers).catch(() => setSellers([]));
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // Mapeia o filtro de status para params do backend.
      // Opções: active | on_time | overdue | done | all
      const params = {
        search: search || undefined,
        assignedTo: filterAssignedTo || undefined,
        createdBy: filterCreatedBy || undefined,
      };

      if (filterStatus === 'active') {
        params.status = 'pending';        // tudo pendente (atrasadas + futuras)
      } else if (filterStatus === 'on_time') {
        params.status = 'pending';
        params.dueFrom = today;           // due_date >= hoje
      } else if (filterStatus === 'overdue') {
        params.dueSpecial = 'overdue';    // backend: pending + due_date < hoje
      } else if (filterStatus === 'done') {
        params.status = 'done';
      }
      // 'all' = sem filtro de status

      // Filtro de data explícito (botão Data ▾) tem precedência
      // sobre o dueFrom implícito do "No prazo".
      if (filterDue) {
        if (filterDue.special) {
          if (filterDue.special === 'today') {
            params.dueFrom = today;
            params.dueTo = today;
          } else {
            params.dueSpecial = filterDue.special;
            delete params.dueFrom;
            delete params.dueTo;
          }
        } else {
          if (filterDue.from) params.dueFrom = filterDue.from;
          if (filterDue.to) params.dueTo = filterDue.to;
        }
      }

      const data = await api.getTasks(params);
      setTasks(data);
    } catch (e) {
      console.error('Erro ao carregar tarefas:', e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterAssignedTo, filterCreatedBy, filterDue, search]);

  useEffect(() => {
    setLoading(true);
    fetchTasks();
  }, [fetchTasks]);

  const contactFilter = searchParams.get('contact_id');

  const showToast = (msg, undoAction) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, undoAction });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  const handleComplete = async (task) => {
    const prev = [...tasks];
    setTasks(tasks.filter(t => t.id !== task.id));
    try {
      await api.updateTask(task.id, { status: 'done' });
      showToast('Tarefa concluída', async () => {
        await api.updateTask(task.id, { status: 'pending' });
        fetchTasks();
      });
    } catch {
      setTasks(prev);
    }
  };

  const handleSnooze = async (task) => {
    try {
      await api.snoozeTask(task.id);
      fetchTasks();
      showToast('Movida para amanhã');
    } catch (e) { console.error(e); }
  };

  const handleReopen = async (task) => {
    try {
      await api.updateTask(task.id, { status: 'pending' });
      fetchTasks();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (task) => {
    const prev = [...tasks];
    setTasks(tasks.filter(t => t.id !== task.id));
    try {
      await api.deleteTask(task.id);
      showToast('Tarefa excluída');
    } catch { setTasks(prev); }
  };

  const handleTaskCreated = (newTask) => {
    setTasks([newTask, ...tasks]);
    setShowCreate(false);
  };

  const today = new Date().toISOString().split('T')[0];
  const next7 = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  let filtered = contactFilter ? tasks.filter(t => t.contact_id === contactFilter) : tasks;

  const groups = [];
  if (filterStatus === 'active') {
    // Tudo pending, com atrasadas em destaque no topo.
    const overdue = filtered.filter(t => t.due_date && t.due_date < today);
    const todayTasks = filtered.filter(t => t.due_date === today);
    const upcoming = filtered.filter(t => t.due_date > today && t.due_date <= next7);
    const future = filtered.filter(t => t.due_date > next7);

    if (overdue.length) groups.push({ title: '🔴 Atrasadas', tasks: sortByPriority(overdue), type: 'overdue' });
    if (todayTasks.length) groups.push({ title: '📌 Hoje', tasks: sortByPriority(todayTasks), type: 'today' });
    if (upcoming.length) groups.push({ title: '📅 Próximos 7 dias', tasks: sortByPriority(upcoming), type: 'upcoming' });
    if (future.length) groups.push({ title: '🗓️ Futuras', tasks: sortByPriority(future), type: 'future' });
  } else if (filterStatus === 'on_time') {
    // Todas já vêm pending + due_date >= hoje do backend.
    // Sub-agrupamos visualmente: Hoje / Próximos 7 dias / Futuras.
    const todayTasks = filtered.filter(t => t.due_date === today);
    const upcoming = filtered.filter(t => t.due_date > today && t.due_date <= next7);
    const future = filtered.filter(t => t.due_date > next7);

    if (todayTasks.length) groups.push({ title: '📌 Hoje', tasks: sortByPriority(todayTasks), type: 'today' });
    if (upcoming.length) groups.push({ title: '📅 Próximos 7 dias', tasks: sortByPriority(upcoming), type: 'upcoming' });
    if (future.length) groups.push({ title: '🗓️ Futuras', tasks: sortByPriority(future), type: 'future' });
  } else if (filterStatus === 'overdue') {
    groups.push({ title: '🔴 Atrasadas', tasks: sortByPriority(filtered), type: 'overdue' });
  } else {
    // 'done' ou 'all': sem sub-agrupamento, lista simples ordenada por prioridade.
    groups.push({ title: '', tasks: sortByPriority(filtered), type: filterStatus });
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey && !showCreate && document.activeElement.tagName !== 'INPUT') {
        e.preventDefault();
        setShowCreate(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showCreate]);

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h1>Tarefas</h1>
        <button className="btn-new-task" onClick={() => setShowCreate(true)}>+ Nova tarefa</button>
      </div>

      <FilterBar
        status={filterStatus}
        onStatusChange={(v) => { setFilterStatus(v); navState.set(sid, 'tarefas', 'filterStatus', v); }}
        assignedTo={filterAssignedTo}
        onAssignedToChange={(v) => { setFilterAssignedTo(v); navState.set(sid, 'tarefas', 'filterAssignedTo', v); }}
        createdBy={filterCreatedBy}
        onCreatedByChange={(v) => { setFilterCreatedBy(v); navState.set(sid, 'tarefas', 'filterCreatedBy', v); }}
        due={filterDue}
        onDueChange={(v) => { setFilterDue(v); navState.set(sid, 'tarefas', 'filterDue', v); }}
        sellers={sellers}
      />

      <div className="tasks-search">
        <input type="text" placeholder="Buscar por título ou contato..." value={search} onChange={(e) => { setSearch(e.target.value); navState.set(sid, 'tarefas', 'search', e.target.value); }} />
      </div>

      {showCreate && (
        <InlineCreateTask
          onCreated={handleTaskCreated}
          onCancel={() => setShowCreate(false)}
          sellers={sellers}
          currentSellerId={currentSellerId}
        />
      )}

      <div className="tasks-list">
        {loading ? (
          <div className="tasks-empty">Carregando...</div>
        ) : groups.length === 0 || groups.every(g => g.tasks.length === 0) ? (
          <div className="tasks-empty">{
            filterStatus === 'done' ? 'Nenhuma tarefa concluída' :
            filterStatus === 'overdue' ? 'Nenhuma tarefa atrasada 🎉' :
            'Nenhuma tarefa 🎉'
          }</div>
        ) : (
          groups.map((group) => (
            <div key={group.title || group.type} className="task-group">
              {group.title && <h3 className={`group-title ${group.type}`}>{group.title}</h3>}
              {group.tasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  sellers={sellers}
                  onComplete={handleComplete}
                  onSnooze={handleSnooze}
                  onReopen={handleReopen}
                  onDelete={handleDelete}
                  onUpdate={fetchTasks}
                  editing={editingId === task.id}
                  onEditStart={() => setEditingId(task.id)}
                  onEditEnd={() => setEditingId(null)}
                  navigate={navigate}
                  today={today}
                />
              ))}
            </div>
          ))
        )}
      </div>

      {toast && (
        <div className="toast">
          <span>{toast.msg}</span>
          {toast.undoAction && <button onClick={() => { toast.undoAction(); setToast(null); }}>Desfazer</button>}
        </div>
      )}
    </div>
  );
}

/* ─── Inline Create ─── */
function InlineCreateTask({ onCreated, onCancel, contactId, conversationId, sellers = [], currentSellerId }) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [priority, setPriority] = useState('normal');
  const [assignedTo, setAssignedTo] = useState(null);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const today = new Date().toISOString().split('T')[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const nextMon = getNextMonday();

  const handleSubmit = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const task = await api.createTask({
        title: title.trim(),
        due_date: dueDate || null,
        priority,
        notes: notes.trim() || null,
        contact_id: contactId || selectedContact?.id || null,
        conversation_id: conversationId || null,
        seller_id: 'default',
        assigned_to: assignedTo || currentSellerId || null,
        created_by: currentSellerId || null,
        attachments: attachments.length > 0 ? attachments : null,
      });
      onCreated(task);
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
    if (e.key === 'Escape') onCancel();
  };

  const searchContacts = async (q) => {
    setContactSearch(q);
    if (q.length < 2) { setContactResults([]); return; }
    try {
      const API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm';
      const resp = await fetch(`${API_URL}/contacts/search?q=${encodeURIComponent(q)}`);
      if (resp.ok) setContactResults(await resp.json());
    } catch { }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const result = await api.uploadFile(file);
        setAttachments(prev => [...prev, {
          url: result.url,
          filename: file.name,
          mimetype: result.mimetype,
          size: result.size,
        }]);
      }
    } catch (err) { console.error('Erro no upload:', err); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="inline-create">
      <input ref={inputRef} type="text" className="create-title" placeholder="O que precisa fazer?" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={handleKeyDown} />
      <div className="create-options">
        <div className="date-chips">
          <button className={`chip ${dueDate === today ? 'selected' : ''}`} onClick={() => setDueDate(today)}>Hoje</button>
          <button className={`chip ${dueDate === tomorrow ? 'selected' : ''}`} onClick={() => setDueDate(tomorrow)}>Amanhã</button>
          <button className={`chip ${dueDate === nextMon ? 'selected' : ''}`} onClick={() => setDueDate(nextMon)}>Seg</button>
          <input type="date" className="chip date-input" value={dueDate || ''} onChange={(e) => setDueDate(e.target.value)} />
          {dueDate && <button className="chip chip-clear" onClick={() => setDueDate('')}>Sem prazo</button>}
        </div>
        <div className="priority-chips">
          <button className={`chip pri-normal ${priority === 'normal' ? 'selected' : ''}`} onClick={() => setPriority('normal')}>Normal</button>
          <button className={`chip pri-medium ${priority === 'medium' ? 'selected' : ''}`} onClick={() => setPriority('medium')}>🟡 Média</button>
          <button className={`chip pri-high ${priority === 'high' ? 'selected' : ''}`} onClick={() => setPriority('high')}>🔴 Alta</button>
        </div>

        {sellers.filter(s => s.id !== currentSellerId).length > 0 && (
          <div className="seller-chips">
            <span className="chip-label">📋 Para:</span>
            {sellers.filter(s => s.id !== currentSellerId).map(s => (
              <button
                key={s.id}
                className={`chip ${assignedTo === s.id ? 'selected' : ''}`}
                style={assignedTo === s.id ? { backgroundColor: s.color, color: '#fff', borderColor: s.color } : {}}
                onClick={() => setAssignedTo(assignedTo === s.id ? null : s.id)}
              >
                {s.name.split(' ')[0]}
              </button>
            ))}
          </div>
        )}

        {!contactId && (
          <div className="contact-search-wrapper">
            {selectedContact ? (
              <div className="selected-contact">
                👤 {selectedContact.name}
                <button onClick={() => { setSelectedContact(null); setContactSearch(''); }}>×</button>
              </div>
            ) : (
              <div className="contact-autocomplete">
                <input type="text" placeholder="👤 Vincular a contato (opcional)" value={contactSearch} onChange={(e) => searchContacts(e.target.value)} />
                {contactResults.length > 0 && (
                  <div className="autocomplete-results">
                    {contactResults.slice(0, 5).map(c => (
                      <button key={c.id} onClick={() => { setSelectedContact(c); setContactResults([]); setContactSearch(''); }}>
                        {c.name} <span className="contact-phone">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showNotes ? (
          <textarea className="create-notes" placeholder="Notas..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        ) : (
          <button className="chip" onClick={() => setShowNotes(true)}>💬 Adicionar nota</button>
        )}

        <div className="attachment-section">
          <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }} />
          <button className="chip" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? '⏳ Enviando...' : '📎 Anexar arquivo'}
          </button>
          {attachments.length > 0 && (
            <div className="attachment-list">
              {attachments.map((att, i) => (
                <div key={i} className="attachment-item">
                  <span className="attachment-icon">📄</span>
                  <span className="attachment-name">{att.filename}</span>
                  <button className="attachment-remove" onClick={() => removeAttachment(i)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="create-actions">
        <button className="btn-cancel" onClick={onCancel}>Cancelar</button>
        <button className="btn-create" onClick={handleSubmit} disabled={!title.trim() || submitting}>{submitting ? 'Criando...' : 'Criar'}</button>
      </div>
    </div>
  );
}

/* ─── Task Item ─── */
function TaskItem({ task, sellers = [], onComplete, onSnooze, onReopen, onDelete, onUpdate, editing, onEditStart, onEditEnd, navigate, today }) {
  const [showMenu, setShowMenu] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDate, setEditDate] = useState(task.due_date || '');
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editAssigned, setEditAssigned] = useState(task.assigned_to || '');
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const handleSaveEdit = async () => {
    try {
      await api.updateTask(task.id, { title: editTitle.trim(), due_date: editDate || null, priority: editPriority, assigned_to: editAssigned || null });
      onEditEnd();
      onUpdate();
    } catch (e) { console.error(e); }
  };

  const isDone = task.status === 'done';
  const isOverdue = !isDone && task.due_date && task.due_date < today;
  const isWaiting = task.status === 'waiting';

  const contactName = task.contacts?.name || null;
  const assignedSeller = sellers.find(s => s.id === task.assigned_to);
  const creatorSeller = sellers.find(s => s.id === task.created_by);
  const showCreator = creatorSeller && (!assignedSeller || creatorSeller.id !== assignedSeller.id);

  if (editing) {
    return (
      <div className="task-item editing">
        <input type="text" className="edit-title" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') onEditEnd(); }} autoFocus />
        <div className="edit-row">
          <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
          <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
            <option value="normal">Normal</option>
            <option value="medium">🟡 Média</option>
            <option value="high">🔴 Alta</option>
          </select>
          {sellers.length > 0 && (
            <select value={editAssigned} onChange={(e) => setEditAssigned(e.target.value)}>
              <option value="">Sem responsável</option>
              {sellers.map(s => <option key={s.id} value={s.id}>{s.name.split(' ')[0]}</option>)}
            </select>
          )}
          <button className="btn-save-edit" onClick={handleSaveEdit}>Salvar</button>
          <button className="btn-cancel-edit" onClick={onEditEnd}>Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`task-item ${isDone ? 'done' : ''} ${isOverdue ? 'overdue' : ''} ${isWaiting ? 'waiting' : ''}`}>
      <div className="task-main">
        {!isDone ? (
          <button className="task-checkbox" onClick={() => onComplete(task)} title="Concluir"><span className="checkbox-inner" /></button>
        ) : (
          <button className="task-checkbox checked" onClick={() => onReopen(task)} title="Reabrir"><span className="checkbox-inner">✓</span></button>
        )}

        <div className="task-content">
          <span className={`task-title ${isDone ? 'strikethrough' : ''}`}>{task.title}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
            {contactName && (
              <span className="task-contact" onClick={() => { console.log("task contact_id:", task.contact_id); navigate(`/app/conversas?contact_id=${task.contact_id}`) }} title="Abrir conversa">
                {contactName}
              </span>
            )}
            {assignedSeller && (
              <span className="task-seller-badge"
                style={{ backgroundColor: assignedSeller.color + '20', color: assignedSeller.color, border: `1px solid ${assignedSeller.color}40` }}
                title={`Responsável: ${assignedSeller.name}`}>
                {assignedSeller.name.split(' ')[0]}
              </span>
            )}
            {showCreator && (
              <span className="task-creator-badge" title={`Criada por ${creatorSeller.name}`}>
                Criada por {creatorSeller.name.split(' ')[0]}
              </span>
            )}
          </div>
          {task.attachments && task.attachments.length > 0 && (
            <div className="task-attachments">
              {task.attachments.map((att, i) => (
                <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="task-attachment-link" title={att.filename}>
                  📎 {att.filename}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="task-meta">
          {task.due_date && !isDone && (
            <span className={`task-date ${isOverdue ? 'overdue' : task.due_date === today ? 'today' : ''}`}>{formatDueDate(task.due_date, today)}</span>
          )}
          {task.priority !== 'normal' && (
            <span className={`task-priority ${task.priority}`}>{task.priority === 'high' ? '🔴' : '🟡'}</span>
          )}
        </div>

        <div className="task-actions">
          {!isDone && task.status === 'pending' && (
            <>
              <button className="action-btn" onClick={() => onSnooze(task)} title="Amanhã">⏭️</button>
            </>
          )}
          {isWaiting && <button className="action-btn" onClick={() => onReopen(task)} title="Reativar">▶️</button>}
          <div className="menu-wrapper" ref={menuRef}>
            <button className="action-btn menu-trigger" onClick={() => setShowMenu(!showMenu)}>···</button>
            {showMenu && (
              <div className="task-menu">
                <button onClick={() => { onEditStart(); setShowMenu(false); }}>✏️ Editar</button>
                {isDone && <button onClick={() => { onReopen(task); setShowMenu(false); }}>🔄 Reabrir</button>}
                <button className="danger" onClick={() => { onDelete(task); setShowMenu(false); }}>🗑️ Excluir</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Filter Bar (Status / Usuário / Criação / Data) ─── */
const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativas' },
  { value: 'on_time', label: 'No prazo' },
  { value: 'overdue', label: 'Atrasadas' },
  { value: 'done', label: 'Concluídas' },
  { value: 'all', label: 'Todas' },
];

function FilterBar({
  status, onStatusChange,
  assignedTo, onAssignedToChange,
  createdBy, onCreatedByChange,
  due, onDueChange,
  sellers,
}) {
  const sellerOptions = [
    { value: '', label: 'Todos' },
    ...sellers.map(s => ({ value: s.id, label: s.name.split(' ')[0] })),
  ];

  const currentStatusLabel =
    STATUS_OPTIONS.find(o => o.value === status)?.label || 'Status';
  const currentAssignedLabel = assignedTo
    ? (sellers.find(s => s.id === assignedTo)?.name.split(' ')[0] || 'Usuário')
    : 'Usuário';
  const currentCreatedLabel = createdBy
    ? (sellers.find(s => s.id === createdBy)?.name.split(' ')[0] || 'Criação')
    : 'Criação';

  return (
    <div className="filter-bar">
      <Dropdown
        label="Status"
        currentLabel={currentStatusLabel}
        options={STATUS_OPTIONS}
        value={status}
        onChange={onStatusChange}
        active={status !== 'active'}
      />
      <Dropdown
        label="Usuário"
        currentLabel={currentAssignedLabel}
        options={sellerOptions}
        value={assignedTo}
        onChange={onAssignedToChange}
        active={!!assignedTo}
      />
      <Dropdown
        label="Criação"
        currentLabel={currentCreatedLabel}
        options={sellerOptions}
        value={createdBy}
        onChange={onCreatedByChange}
        active={!!createdBy}
      />
      <DateFilter due={due} onChange={onDueChange} />
    </div>
  );
}

/* ─── Dropdown genérico ─── */
function Dropdown({ label, currentLabel, options, value, onChange, active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className={`filter-dropdown ${active ? 'active' : ''}`} ref={ref}>
      <button
        className="filter-btn"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span className="filter-btn-label">{active ? currentLabel : label}</span>
        <span className="filter-btn-caret">▾</span>
      </button>
      {open && (
        <div className="filter-menu">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`filter-option ${value === opt.value ? 'selected' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Date Filter (texto = hoje, caret = calendário range/dia) ─── */
function DateFilter({ due, onChange }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('range'); // 'range' | 'day'
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isActive = !!due;

  // Label do botão (estado ativo)
  let activeLabel = 'Data';
  if (due) {
    if (due.special === 'today') {
      activeLabel = 'Hoje';
    } else if (due.special === 'overdue') {
      activeLabel = 'Atrasadas';
    } else if (due.special === 'no_date') {
      activeLabel = 'Sem prazo';
    } else if (due.from && due.to) {
      if (due.from === due.to) {
        activeLabel = formatBR(due.from);
      } else {
        activeLabel = `${formatBR(due.from)} – ${formatBR(due.to)}`;
      }
    }
  }

  const handleTextClick = () => {
    // Clica no texto → filtra hoje instantaneamente
    onChange({ special: 'today' });
    setOpen(false);
  };

  const handleCaretClick = (e) => {
    e.stopPropagation();
    setOpen(o => !o);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setRangeStart('');
    setRangeEnd('');
  };

  const applyRange = () => {
    if (!rangeStart) return;
    if (mode === 'day') {
      onChange({ from: rangeStart, to: rangeStart });
    } else {
      onChange({ from: rangeStart, to: rangeEnd || rangeStart });
    }
    setOpen(false);
  };

  return (
    <div className={`filter-dropdown date-filter ${isActive ? 'active' : ''}`} ref={ref}>
      <div className="filter-btn date-btn">
        <button
          type="button"
          className="filter-btn-label-zone"
          onClick={handleTextClick}
        >
          {activeLabel}
        </button>
        {isActive && (
          <button
            type="button"
            className="filter-btn-clear"
            onClick={handleClear}
            title="Limpar filtro"
          >×</button>
        )}
        <button
          type="button"
          className="filter-btn-caret-zone"
          onClick={handleCaretClick}
        >▾</button>
      </div>

      {open && (
        <div className="filter-menu date-menu">
          <div className="date-quick">
            <button type="button" onClick={() => { onChange({ special: 'overdue' }); setOpen(false); }}>Atrasadas</button>
            <button type="button" onClick={() => { onChange({ special: 'no_date' }); setOpen(false); }}>Sem prazo</button>
          </div>
          <div className="date-divider" />
          <div className="date-range-inputs">
            {mode === 'range' ? (
              <>
                <label>De
                  <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
                </label>
                <label>Até
                  <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
                </label>
              </>
            ) : (
              <label>Dia
                <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
              </label>
            )}
          </div>
          <div className="date-actions">
            <button
              type="button"
              className="date-mode-toggle"
              onClick={() => setMode(m => m === 'range' ? 'day' : 'range')}
            >
              {mode === 'range' ? 'Dia' : 'Range'}
            </button>
            <button
              type="button"
              className="date-apply"
              onClick={applyRange}
              disabled={!rangeStart}
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatBR(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/* ─── Helpers ─── */
function sortByPriority(tasks) {
  const order = { high: 0, medium: 1, normal: 2 };
  return [...tasks].sort((a, b) => (order[a.priority] || 2) - (order[b.priority] || 2));
}

function getNextMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function formatDueDate(dateStr, today) {
  if (dateStr === today) return 'Hoje';
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  if (dateStr === tomorrow) return 'Amanhã';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export { InlineCreateTask };
export default TasksPage;
